import { ReactNode } from "react";
import styled from "styled-components";

import { adminFontFamily, adminTokens } from "@pages/AdminDashboard/theme/adminTokens";

const { color, typeScale } = adminTokens;
const [spaceXs] = adminTokens.spacing;

// The design sets the first identifier in Light 22 (STRIP-R3); like the card
// title it is not one of the eight fixed text styles yet.
const primaryIdentifierSize = "22px";
// Row and column gaps as the design sets them between identifiers.
const identifierGap = "10px 28px";

const Strip = styled.div.attrs({ role: "group" })`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: ${identifierGap};
  padding: 0 ${spaceXs}px;
  color: ${color.inkSoft};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
`;

// An identifier may shrink and break inside a long word, so a long one wraps
// instead of pushing the page sideways (PHONE-R2).
const Identifier = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  color: ${color.inkSoft};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
`;

const PrimaryIdentifier = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  color: ${color.navy};
  font-family: ${adminFontFamily.light};
  font-size: ${primaryIdentifierSize};
`;

interface ContextStripProps {
  label: string;
  identifiers: ReactNode[];
}

// One line of identifiers above Redo and Ship content, for example the case
// number, the patient, the trip and the invoice; an identifier may hold more than
// text, such as a status badge (STRIP-R1…R3, R5). The first
// identifier leads; the rest read in the order given.
export const ContextStrip = ({ label, identifiers }: ContextStripProps) => {
  const [primary, ...others] = identifiers;

  return (
    <Strip aria-label={label}>
      <PrimaryIdentifier>{primary}</PrimaryIdentifier>
      {others.map((identifier, position) => (
        <Identifier key={`${position}-${identifier}`}>{identifier}</Identifier>
      ))}
    </Strip>
  );
};
